import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  AccessibleTenant,
  ConnectorDto,
  ConnectorStatus,
  CreateConnectorInput,
  IndicatorDto,
  MeResponse,
  SourceSensorDto,
  TenantDto,
  TestConnectorInput,
  UpdateConnectorInput,
} from '@atnt/shared';
import { api } from '@/shared/api/client';
import { deviceKeys } from '@/features/devices/api';
import type { ConnectorsBackend, ConnectorsContext } from './backend';
import { httpBackend } from './http';
import { connectorKeys, connectorStatusQuery, tenantBase, tenantDetailsQuery } from './keys';
import { mockBackend } from './mock';

export { connectorKeys } from './keys';

/**
 * Connectors data layer.
 *
 * THE SWITCH: `USE_MOCK_CONNECTORS = false` routes every hook below to the real API (`httpBackend`). Until the API
 * implements it, the in-browser mock (features/connectors/mock) serves the UI. Endpoints the backend must implement
 * (under /api/v1/tenants/:tenantId, contract + DTOs in packages/shared/src/schemas/connectors.ts):
 *   GET    /connectors                                 list (organization connectors of the tenant's org + its own)
 *   POST   /connectors                                 create
 *   GET    /connectors/:id                             one
 *   PATCH  /connectors/:id                             update (also `{ enabled }`; secrets omitted = kept)
 *   DELETE /connectors/:id                             delete
 *   POST   /connectors/test                            test a draft config (create form)
 *   POST   /connectors/:id/test                        test the stored connector (optional draft config)
 *   GET    /connectors/:id/sensors                     source sensors (node groups, last raw value, binding)
 *   PUT    /connectors/:id/sensors/:sensorId/binding   bind / unbind `{ indicatorId | null }`
 *   POST   /connectors/:id/sensors/import              create devices + indicators for unbound sensors
 *   GET    /connectors/:id/log                         sync / test / change log
 * Already real (kept from the first version of the page): `GET /connector-status`, `GET|PATCH /tenants/:t`
 * (`externalSiteId`, organization admins), `PATCH /devices/:id/indicators/:indicatorId { externalChannelId }`.
 */
const USE_MOCK_CONNECTORS = true;
const backend: ConnectorsBackend = USE_MOCK_CONNECTORS ? mockBackend : httpBackend;

/** Context every connectors hook takes (tenant, user, query client). */
export function useConnectorsContext(tenant: AccessibleTenant, me: MeResponse): ConnectorsContext {
  const qc = useQueryClient();
  return { tenant, me, qc };
}

/* ───────────── connectors ───────────── */

export function useConnectors(ctx: ConnectorsContext) {
  return useQuery({
    queryKey: connectorKeys.list(ctx.tenant.id),
    queryFn: () => backend.list(ctx),
    refetchInterval: 30_000,
  });
}

export function useConnector(ctx: ConnectorsContext, id: string) {
  return useQuery({
    queryKey: connectorKeys.detail(ctx.tenant.id, id),
    queryFn: () => backend.get(ctx, id),
    placeholderData: () => ctx.qc.getQueryData<ConnectorDto[]>(connectorKeys.list(ctx.tenant.id))?.find((c) => c.id === id),
    refetchInterval: 30_000,
  });
}

/** Write a connector into the list and detail caches. */
function putConnector(qc: QueryClient, tenantId: string, c: ConnectorDto) {
  qc.setQueryData(connectorKeys.detail(tenantId, c.id), c);
  qc.setQueryData<ConnectorDto[]>(connectorKeys.list(tenantId), (old) => (old ? (old.some((x) => x.id === c.id) ? old.map((x) => (x.id === c.id ? c : x)) : [...old, c]) : old));
}

/** Optimistic status in both caches (e.g. "Tekshirilmoqda" while a test runs). */
function patchStatus(qc: QueryClient, tenantId: string, id: string, patch: Partial<Pick<ConnectorDto, 'status' | 'enabled'>>) {
  qc.setQueryData<ConnectorDto>(connectorKeys.detail(tenantId, id), (old) => (old ? { ...old, ...patch } : old));
  qc.setQueryData<ConnectorDto[]>(connectorKeys.list(tenantId), (old) => old?.map((c) => (c.id === id ? { ...c, ...patch } : c)));
}

const invalidateConnector = (qc: QueryClient, tenantId: string) => qc.invalidateQueries({ queryKey: connectorKeys.all(tenantId) });

export function useSaveConnector(ctx: ConnectorsContext) {
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: CreateConnectorInput | UpdateConnectorInput }) =>
      id ? backend.update(ctx, id, input as UpdateConnectorInput) : backend.create(ctx, input as CreateConnectorInput),
    onSuccess: (c) => {
      putConnector(ctx.qc, ctx.tenant.id, c);
      void invalidateConnector(ctx.qc, ctx.tenant.id);
    },
  });
}

export function useDeleteConnector(ctx: ConnectorsContext) {
  return useMutation({
    mutationFn: (id: string) => backend.remove(ctx, id),
    onSuccess: (_, id) => {
      ctx.qc.setQueryData<ConnectorDto[]>(connectorKeys.list(ctx.tenant.id), (old) => old?.filter((c) => c.id !== id));
      ctx.qc.removeQueries({ queryKey: connectorKeys.detail(ctx.tenant.id, id) });
      void invalidateConnector(ctx.qc, ctx.tenant.id);
    },
  });
}

export function useToggleConnector(ctx: ConnectorsContext) {
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => backend.update(ctx, id, { enabled }),
    onMutate: ({ id, enabled }) => patchStatus(ctx.qc, ctx.tenant.id, id, { enabled, status: (enabled ? 'checking' : 'disabled') as ConnectorStatus }),
    onSuccess: (c) => putConnector(ctx.qc, ctx.tenant.id, c),
    onSettled: () => invalidateConnector(ctx.qc, ctx.tenant.id),
  });
}

/** `id` + no config = test the stored connector (its status follows); a config = test a draft (form). */
export function useTestConnector(ctx: ConnectorsContext) {
  return useMutation({
    mutationFn: ({ id, input }: { id: string | null; input?: TestConnectorInput }) => backend.test(ctx, id, input ?? {}),
    onMutate: ({ id, input }) => {
      if (id && !input?.config) patchStatus(ctx.qc, ctx.tenant.id, id, { status: 'checking' });
    },
    onSettled: (_r, _e, { id }) => (id ? invalidateConnector(ctx.qc, ctx.tenant.id) : undefined),
  });
}

/* ───────────── source sensors / log ───────────── */

export function useSourceSensors(ctx: ConnectorsContext, id: string) {
  return useQuery({ queryKey: connectorKeys.sensors(ctx.tenant.id, id), queryFn: () => backend.sensors(ctx, id), refetchInterval: 60_000 });
}

export function useBindSourceSensor(ctx: ConnectorsContext, id: string) {
  return useMutation({
    mutationFn: ({ sensorId, indicatorId }: { sensorId: string; indicatorId: string | null }) => backend.bind(ctx, id, sensorId, indicatorId),
    onSuccess: (sensor) => {
      ctx.qc.setQueryData<SourceSensorDto[]>(connectorKeys.sensors(ctx.tenant.id, id), (old) =>
        old?.map((s) => (s.id === sensor.id ? sensor : sensor.binding && s.binding?.indicatorId === sensor.binding.indicatorId ? { ...s, binding: null } : s)),
      );
      void invalidateConnector(ctx.qc, ctx.tenant.id);
    },
  });
}

export function useImportSourceSensors(ctx: ConnectorsContext, id: string) {
  return useMutation({
    mutationFn: (sensorIds: string[]) => backend.importSensors(ctx, id, sensorIds),
    onSettled: () => ctx.qc.invalidateQueries({ queryKey: connectorKeys.log(ctx.tenant.id, id) }),
  });
}

export function useConnectorLog(ctx: ConnectorsContext, id: string) {
  return useQuery({ queryKey: connectorKeys.log(ctx.tenant.id, id), queryFn: () => backend.log(ctx, id), refetchInterval: 60_000 });
}

/* ───────────── real: unified-server status, site code, channel mapping ───────────── */

export const useConnectorStatus = (tenantId: string) => useQuery(connectorStatusQuery(tenantId));
export const useTenantDetails = (tenantId: string) => useQuery(tenantDetailsQuery(tenantId));

export function useSaveSiteId(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (externalSiteId: string | null) => api.patch<TenantDto>(tenantBase(tenantId), { externalSiteId }),
    onSuccess: (tenant) => {
      qc.setQueryData(connectorKeys.tenant(tenantId), tenant);
      void qc.invalidateQueries({ queryKey: connectorKeys.status(tenantId) });
      void qc.invalidateQueries({ queryKey: connectorKeys.all(tenantId) });
    },
  });
}

export function useSaveChannel(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceId, indicatorId, channel }: { deviceId: string; indicatorId: string; channel: string | null }) =>
      api.patch<IndicatorDto>(`${tenantBase(tenantId)}/devices/${encodeURIComponent(deviceId)}/indicators/${encodeURIComponent(indicatorId)}`, {
        externalChannelId: channel,
      }),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: deviceKeys.devices(tenantId) }),
        qc.invalidateQueries({ queryKey: connectorKeys.status(tenantId) }),
        qc.invalidateQueries({ queryKey: connectorKeys.all(tenantId) }),
      ]),
  });
}
